// import App from "next/app";
import 'reflect-metadata';
import '../styles/globals.scss';

import App from 'next/app';
import Head from 'next/head';
import { createElement, createRef, Fragment } from 'react';
import { from } from 'rxjs';
import { delay, filter, mergeMap, retry, retryWhen, take } from 'rxjs/operators';

import dynamic from 'next/dynamic';
import { Commons } from '../commons/commons';
const FooterComponent = dynamic(() => import('../components/footer/footer.component').then(mod => ({ default: mod.FooterComponent })));
const HeaderComponent = dynamic(() => import('../components/header/header.component'));
import { DIProvider } from '../context/ioc';
import { container, lazyInject } from '../context/module';
import { MetricsService } from '../services/metrics.service';
import { scheduleCoordinator } from '../services/scheduling/schedule-coordinator';
import { scheduleTierOf } from '../services/scheduling/schedule-tier';
import { initWebVitals } from '../utils/web-vitals';
import { Logger } from '../models/logger';
import { GAService } from '../services/analytics.service';
import { ApiConfig } from '../services/api-config';
import { DOMStorage } from '../services/dom-storage.service';
import { EnvironmentService } from '../services/environment.service';
import { FileUploadRecoveryService } from '../services/file-upload-recovery.service';
import { transferTray } from '../services/transfer-tray.service';
import { FileUploadService } from '../services/file-upload.service';
import { IdentityUnavailableError, LightHttp } from '../services/light-http.service';
import { LogService } from '../services/log.service';
import { LoginService } from '../services/login.service';
import { bootstrapSession } from '../services/session-bootstrap';
import { noticeIdentityUnavailable } from '../services/identity-notice';
import { MetadataService } from '../services/metadata.service';
import { MotivesService } from '../services/motives.service';
import { ScriptLoadService } from '../services/script-load.service';
import { SettingsService } from '../services/settings.service';
import { UIContext } from '../services/ui-context';
import { WebSocketService } from '../services/websocket.service';
import { WindowService } from '../services/window.service';
import { appPropsToRouteProps, RouteProps } from '../types/route-types';
import { BrowserUtils } from '../ui-utils/browser-utils';
import { get, isEqual } from '../utils/lodash-min';
import { ClientError, MotiveConfig, NetworkError } from '../utils/request.utils';
import { RouteUtils } from '../utils/route.utils';
import { StringUtils } from '../utils/string-utils';
import { WindowUtils } from '../utils/window.utils';
import { ConnectivityUtils } from '../utils/connectivity.utils';
import ErrorPage from './_error';
import { SpinnerService } from '../services/spinner.service';
const SpinnerModal = dynamic(() => import('../components/ui/spinner/spinner.modal'));
const EnforcementErrorModal = dynamic(
    () => import('../components/ui/enforcement-error-modal/enforcement-error-modal.component').then(mod => ({ default: mod.EnforcementErrorModal })),
    { ssr: false },
);
import { UserTraceService } from '../services/user-trace.service';
import Script from 'next/script';
import { withRequestIdleCallback } from '../annotations/request-idle-callback.annotation';
import { ConnectivityService } from '../services/connectivity.service';
import { internalLinkAttrs } from '../utils/link-metrics';
import { MetricEventName } from '../types/metric-events';
const Navbar = dynamic(() => import('../components/header/header-v2.component'));
const FooterV2Component = dynamic(() => import('../components/footer/footer-v2.component'));
const DownloadsCenter = dynamic(() => import('../components/downloads/downloads-center/downloads-center.component').then(mod => ({ default: mod.DownloadsCenter })));
// Round 13 (Z4, P4): where a client-side navigation lands -- push = top, Back = the remembered position.
import { bindPageLanding } from '../components/downloads/page-landing';
import { hasSkipLink, SkipLink } from '../components/pdf-shared/skip-link'; // W8-SHELL (UX-10)
// Site-wide sticky, closable bottom anchor (our own manual unit). Client-only: it renders null
// until the effective motive settings resolve, reads localStorage for its dismissal, and pushes to
// adsbygoogle -- all browser-side. See components/ui/motive/anchor-motive.component.tsx.
const AnchorAd = dynamic(() => import('../components/ui/motive/anchor-motive.component').then(mod => ({ default: mod.AnchorMotiveComponent })), { ssr: false });

interface MyAppState {
    fileSelectBtnClicked: boolean;
}
export default class MyApp extends App<any, any, MyAppState> {
    private readonly logger = Logger.getLogger('MyApp');

    fileInputRef = createRef<HTMLInputElement>();

    /**Need this here just to initialize the logger */
    @lazyInject(LogService) private logService: LogService;
    @lazyInject(LoginService) private loginService: LoginService;
    @lazyInject(EnvironmentService) private environmentService: EnvironmentService;
    @lazyInject(WindowService) private windowService: WindowService;
    @lazyInject(MotivesService) private motivesService: MotivesService;
    @lazyInject(SettingsService) private settingsService: SettingsService;
    @lazyInject(WebSocketService) private webSocketService: WebSocketService;
    @lazyInject(DOMStorage) private domStorage: DOMStorage;
    @lazyInject(FileUploadService) private fileUploadService: FileUploadService;
    @lazyInject(FileUploadRecoveryService) private fileUploadRecoveryService: FileUploadRecoveryService;
    @lazyInject(UIContext) private uiContext: UIContext;
    @lazyInject(ApiConfig) private apiConfig: ApiConfig;
    @lazyInject(LightHttp) private lightHttp: LightHttp;
    @lazyInject(ScriptLoadService) private scriptLoadService: ScriptLoadService;
    @lazyInject(MetadataService) private metadataService: MetadataService;
    @lazyInject(GAService) private gaService: GAService;
    @lazyInject(SpinnerService) private spinnerService: SpinnerService;
    @lazyInject(UserTraceService) private userTraceService: UserTraceService;
    @lazyInject(ConnectivityService) private connectivityService: ConnectivityService;

    // @lazyInject(FileInputButtonService) private fileInputButtonService: FileInputButtonService;

    canShowMotives: boolean;
    sitename: string;
    siteDisplayName: string;
    effectiveMotives: MotiveConfig;

    downloadableFileCount = 0;

    // main.js version
    version = 1.023;
    // index.html version
    local_version = 0.000;
    // actual version from mongo
    remote_version = 0.000;

    shouldReload = false;

    performedVersionCheckAndAutoLogin = false;
    extensionInstalled = false;

    showExtension: boolean;
    helpMaximized = false;

    currentUrl: string | null = null;
    canGoOffline: boolean;

    routeProps: RouteProps;

    get isIOS() {
        return BrowserUtils.isIOS();
    }
    get isAndroid() {
        return BrowserUtils.isAndroid();
    }

    readonly state: Readonly<MyAppState> = { fileSelectBtnClicked: false };
    constructor(public props) {
        super(props);
        this.environmentService.loadEnvironmentFromCLIData(props);
        this.logService.initialize();
        if (props.pageProps.statusCode === 404) {
            return;
        }
        this.routeProps = appPropsToRouteProps(props);
        this.motivesService.initialMotives = get(this.routeProps, 'settings.motive');
        this.settingsService.initializeFromProps(this.routeProps);
        // this.metadataService.setMetaTags(this.routeProps.pageData);
        // this.recordCLS();
        this.init();
        const componentMountedTime = Date.now();
        this.windowService.onFileSelectBtnClicked.pipe(
            filter(evt => evt.dispatchedAt.getTime() > componentMountedTime),
        ).subscribe({
            next: () => {
                if (!this.state.fileSelectBtnClicked) {
                    this.setState({
                        fileSelectBtnClicked: true,
                    });
                }
            }
        });
        // Defer WebSocket initialization to after mount
    }

    componentDidMount(): void {
        this.userTraceService.trace("App component mounted");

        // QA-only ad-slot visualiser: on the QA host, mark <html> so globals.scss paints every
        // reserved ad box (filled or not) for placement review. Never set on the prod host.
        // Round 13 (Studio S, R12 QA): OPT-IN -- `?adDebug=1` turns it on for this browser (kept in
        // localStorage), `?adDebug=0` turns it off; without the flag QA shows real ads, as production
        // does (the owner reviews on QA and read the dashed purple boxes as "ads not displayed properly").
        if (BrowserUtils.hasWindow()) {
            try {
                const host = window.location.hostname || '';
                if (host.startsWith('qa.') || host.includes('qa.xconvert')) {
                    const flag = new URLSearchParams(window.location.search).get('adDebug');
                    if (flag === '1') window.localStorage.setItem('xc_ad_debug', '1');
                    else if (flag === '0') window.localStorage.removeItem('xc_ad_debug');
                    if (window.localStorage.getItem('xc_ad_debug') === '1') {
                        document.documentElement.setAttribute('data-xc-ad-debug', '1');
                    }
                }
            } catch {
                // ignore
            }
        }

        // Round 13 (Z4, owner review 5 P4): `<body>` is this site's scroller, so Next's own
        // `window.scrollTo(0, 0)` after a push never moved anything and Back restored nothing. A push to
        // a new pathname now lands at the top; Back/forward puts back the position that entry was left
        // at; query-only tab writes are untouched. components/downloads/page-landing.ts.
        if (BrowserUtils.hasWindow()) {
            bindPageLanding();
        }

        // Initialize metrics service with page unload handlers
        if (BrowserUtils.hasWindow()) {
            const metrics = container.get(MetricsService);
            window.addEventListener('pagehide', () => metrics.flushOnUnload());
            window.addEventListener('beforeunload', () => metrics.flushOnUnload());
            // Field Core Web Vitals, 10% sampled. Registered here (right after
            // the unload flush is wired) because CLS and INP finalise on
            // visibility change and ride out on that same flush.
            initWebVitals();

            // One delegated listener for internal navigation. Registered in the capture
            // phase so a handler that calls stopPropagation (the ad dismissal, the tool
            // pickers) cannot silence it, and passive so it can never delay the click.
            // Attributes are bounded closed sets — see utils/link-metrics.ts for why the
            // href itself must never be emitted. Overlap with the named CTA events
            // (NEXT_TOOL_CLICKED and friends) is deliberate: those measure one specific
            // control, this measures navigation.
            document.addEventListener('click', (event: MouseEvent) => {
                try {
                    const target = event.target as Element | null;
                    const link = target && typeof target.closest === 'function'
                        ? target.closest('a')
                        : null;
                    const attrs = internalLinkAttrs(link, window.location.host);
                    if (attrs) metrics.emit(MetricEventName.INTERNAL_LINK_CLICK, attrs);
                } catch {
                    // a metrics failure must never break navigation
                }
            }, { capture: true, passive: true });
        }

        // Initialize WebSocket early to avoid IOC initialization errors
        this.webSocketService.initWS();

        // Initialize analytics tracking after mount to reduce initial load
        if (BrowserUtils.hasWindow()) {
            WindowUtils.runWhenIdeal(() => {
                // Dynamically import Clarity to avoid bundling it in main chunk
                import('@microsoft/clarity').then(({ default: Clarity }) => {
                    Clarity.init('qpfzjffr8a');
                    const wsId = this.webSocketService.getOrCreateStoredWSId();
                    Clarity.identify(wsId);
                }).catch(console.error);
            });
        }

        // Adsense loading is handled by MotivesService.loadMotiveSource()
        // Listen for online/offline events (debounced to avoid transient blips)
        let offlineTimer: any = null;
        ConnectivityUtils.onStatusChange((online) => {
            if (!this.uiContext) return;
            if (!online) {
                offlineTimer = setTimeout(() => {
                    this.uiContext.warnToast('Connection Lost', 'You appear to be offline.', false, false);
                }, 5000);
            } else {
                if (offlineTimer) { clearTimeout(offlineTimer); offlineTimer = null; }
                this.uiContext.successToast('Back Online', 'Your connection has been restored.');
            }
        });

        // Re-validate session when the tab regains focus. Detects server-side session
        // invalidation (account deleted, password changed elsewhere, token revoked)
        // that happened while the tab was in the background. Throttled so rapid tab
        // switching doesn't spam /auth/login.
        if (BrowserUtils.hasWindow()) {
            let lastRevalidateAt = Date.now();
            const MIN_INTERVAL_MS = 30 * 1000;
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState !== 'visible') return;
                if (Date.now() - lastRevalidateAt < MIN_INTERVAL_MS) return;
                lastRevalidateAt = Date.now();
                this.loginService.login(null, null, null, { background: true }).subscribe({
                    error: (err) => {
                        // login service + _app error handler already self-heal on 401/403.
                        // Don't show a Retry dialog here \u2014 visibility is a background event.
                        this.logger.info({ message: 'Background session revalidate failed', error: err });
                    },
                });
            });
        }
    }

    componentDidUpdate() {
        // Disabled since GA4 Enhanced measurement track this
        // this.gaService.pageview(this.routeProps?.url);
    }

    private init() {
        const { Component, pageProps } = this.props;
        if (this.props.pageProps.statusCode === 404) {
            return;
        }
        if (!pageProps.url) {
            console.error('Page loaded without a url', pageProps);
            return;
        }

        this.sitename = this.environmentService.env.sitename;
        this.siteDisplayName = this.environmentService.env.siteDisplayName;

        this.motivesService.onEffectiveMotiveSettingsChange.subscribe(
            (motives) => {
                this.showExtension = isEqual(get(BrowserUtils.getBrowserInfo(), 'name'), 'chrome') && !BrowserUtils.isMobile() &&
                    motives && motives.showExtensionDownloadDialog;
                this.effectiveMotives = motives;
            },
            (error) => this.logger.error(error),
        );
        this.loginService.onUserDetailsChanged.subscribe(
            (accountDetails) => {
                this.handleAccountDetails(accountDetails);
            },
        );

        if (BrowserUtils.hasWindow()) {
            // ERRORS-12 (W8-SHELL): the only bare subscribe in the shell — its `timeout(4000)` on the
            // settings replay (no settings yet, e.g. offline at load) was an unhandled rxjs error.
            // The service already treats it as "not important"; so does its caller now.
            this.windowService.initialize().subscribe({ error: () => undefined });
        }

        const currentPagePath = this.props.path;
        let isValidCapability = false;
        let featurePagePath = null;
        try {
            featurePagePath = currentPagePath.match(/[\-0-9a-zA-Z]+/)[0];
            // TODO disabled for preact
            // isValidCapability = this.capabilityServie.hasCapability(currentPagePath);
        } catch (e) {
            // suppressed
        }

        WindowUtils.runWhenIdeal(() => {
            this.fileUploadService.initialize();
        });

        WindowUtils.runWhenIdeal(() => {
            this.fileUploadRecoveryService.initialize();
        });

        WindowUtils.runWhenIdeal(() => {
            // Drop hand-off records nobody picked up, so abandoned file bytes do not sit in
            // IndexedDB indefinitely. Same boot slot as the upload-recovery sweep.
            transferTray.sweepExpired().catch(() => { /* best-effort */ });
        });

        if (BrowserUtils.hasWindow()) {
            this.setUpLoginService();
            // this.setUpVersionCheck();
            this.setUpPreferredDomains();
            this.setUpSettingsService();
            this.setUpGoogleAnalytics();
        }

        WindowUtils.runWhenIdeal(() => {
            this.motivesService.initialize();
        });

        // Load Bootstrap JS immediately for dropdown functionality
        if (BrowserUtils.hasWindow()) {
            this.scriptLoadService.loadBootstrapJS().catch(console.log);
        }

        // Load image processing libs after user interaction (not critical for initial render)
        WindowUtils.runWhenIdeal(() => {
            this.windowService.userClicked.pipe(take(1)).subscribe(() => {
                this.scriptLoadService.loadImageProcessingLibs().catch(console.log);
            });
        });

        this.domStorage.setAppComponentReadyAndFreezeContentIfNeeded();
    }

    private handleAccountDetails(accountDetails) {
        // Code 10 (a): the schedule gate paces VIDEO work at the sold concurrency (FREE/BASIC 4, PRO 8).
        // This is the one place every page already hears the account.
        try { scheduleCoordinator.setTier(scheduleTierOf(accountDetails)); } catch { /* pacing is best-effort */ }
        if (accountDetails) {
            this.canShowMotives = this.windowService && this.windowService.mousedowned &&
                this.motivesService.motiveReady && this.settingsService.motivesEnabled &&
                !accountDetails.isPaidUser();
        }
        this.canGoOffline = this.loginService.canGoOffline;
    }

    @withRequestIdleCallback
    private setUpSettingsService() {
        this.settingsService.initialize().pipe(
            retry({ count: 2, delay: 1000 }),
        ).subscribe({
            next: () => {
                // no-op
            },
            error: (e) => {
                this.logger.error(e);
            }
        })
    }

    @withRequestIdleCallback
    private setUpPreferredDomains() {
        this.connectivityService.initialize$().subscribe({
            error: (e) => {
                this.logger.error({
                    message: 'Failed to initialize preferred domains',
                    error: e
                })
            }
        })
    }

    @withRequestIdleCallback
    private setUpLoginService(spinner: any = null) {
        this.initializeWSService$();
        // bootstrapSession() is the boot exchange: flag-OFF it returns the plain
        // login(null,null,null) (today's behavior, byte-for-byte); flag-ON it
        // adds the legacy-bearer->session migration + refresh-token machinery
        // wiring. Error/spinner handling below is unchanged.
        bootstrapSession().subscribe({
            next: () => {
                if (spinner) {
                    this.spinnerService.hide(spinner);
                    spinner = null
                    this.uiContext.successToast("Successfully initialized services!")
                }
            },
            error: (err) => {
                if (spinner) {
                    this.spinnerService.hide(spinner);
                    spinner = null
                }

                const status = ClientError.is(err) ? (err as ClientError).status : 0;
                if (status === 401 || status === 403) {
                    // Auth rejected our bearer. Retrying will not help. Clear the token
                    // so the next page load starts anonymous and tell the user to log in.
                    this.loginService.clearLocalSession();
                    this.uiContext.showErrorDialog({
                        title: 'Session expired',
                        text: 'Your session has expired. Please log in again to continue.',
                    });
                    this.logger.info({ message: 'Auto-login rejected', data: { status } });
                    return;
                }

                // F3r (review 7): the sign-in give-up (429) is a NON-BLOCKING notice, never this dialog -- the finished
                // downloads need no identity, and the dialog's Retry re-opened itself while the IP was still limited.
                if (err instanceof IdentityUnavailableError) {
                    noticeIdentityUnavailable({ uiContext: this.uiContext, loginService: this.loginService }, err);
                    this.logger.info({ message: 'Anonymous sign-in gave up on 429; notice shown', error: err });
                    return;
                }

                if (NetworkError.is(err)) {
                    err.message = 'Unable to initialize services. Please connect and reload the page again!';
                }
                this.uiContext.handleError(
                    err,
                    {
                        confirmButtonText: "Retry",
                        confirmButtonCallback: async () => {
                            this.setUpLoginService(await this.spinnerService.show("Retrying..."))
                        }
                    }
                );
                this.logger.error(err);
            }
        });
    }

    @withRequestIdleCallback
    initializeWSService$(wsDomains: string[] = []) {
        this.webSocketService.reinitialize(wsDomains).then(() => {
            this.webSocketService.initialize();
        }).catch((err) => {
            console.error('Failed to initialize WebSocket service', err);
        });

        // let init$ = this.windowService.userClicked;
        // if (this.routeProps.url && this.routeProps.url.startsWith('/downloads')) {
        //     init$ = this.windowService.userEngaged;
        // }
        // init$.pipe(
        //     mergeMap(() => {
        //         return from(this.webSocketService.reinitialize(wsDomains));
        //     }),
        // ).subscribe({
        //     next: () => {
        //         this.webSocketService.initialize();
        //     },
        //     error: () => {
        //         console.log('Failed to initialize WS');
        //     }
        // });
    }

    private setUpVersionCheck() {
        // await this.scriptLoadService.loadAllScriptsIfNotLoaded();
        let versionLogged = false;
        const updateCheckFn = () => {
            // this.updateExtensionStatus(); // TODO PReact disabled
            this.windowService.userClicked.subscribe({
                next: () => {
                    this.lightHttp.get(this.apiConfig.getVersionCheckEndpoint()).pipe(
                        retryWhen(errors => errors.pipe(delay(1000), take(2))),
                    ).subscribe({
                        next: (data: any) => {
                            this.remote_version = data.web_version;
                            if (!versionLogged) {
                                this.logger.trace1({
                                    message: `HTML version: ${this.local_version}, JS version: ${this.version} and Remote version: ${this.remote_version}`,
                                });
                                versionLogged = true;
                            }
                            // this.updateShouldReload(); // TODO PReact disabled
                        },
                        error: (err) => {
                            this.logger.trace7({
                                message: 'Unable to get the web version',
                                data: err,
                            });
                        }
                    })
                }
            });
        };

        // Initially poll every second for first 20 seconds
        let interval = setInterval(updateCheckFn, 20000);
        setTimeout(() => {
            clearInterval(interval);
            // Fallback to every one minute
            interval = setInterval(updateCheckFn, 20000 * 60);
        }, 1000 * 20);
    }

    @withRequestIdleCallback
    setUpGoogleAnalytics() {
        // Using Rx's built in `distinctUntilChanged ` feature to handle url change c/o @dloomb's answer
        if (BrowserUtils.hasWindow()) {
            let ga = this['ga'];
            try {
                ga = window && window['ga'];
            } catch (e) {
                this.logger.error(e);
            }
            if (ga) {
                ga('set', 'page', this.currentUrl);
            }
        }
    }

    getComponent(props: RouteProps) {
        if (!props || !props.url) {
            return ErrorPage;
        }
        const pathname = RouteUtils.pathTextOnly(props.url);
        if (BrowserUtils.hasWindow()) {
            const windowPath = RouteUtils.pathTextOnly(window.location.pathname);
            if (windowPath !== pathname) {
                // React migrate
                // return <NotFoundPage></NotFoundPage>
            }
        }
        this.loadRouteSpecificScripts(pathname);

        // switch (pathname) {
        //     case '':
        //         return <HomeComponent {...props}></HomeComponent>;
        //     case 'downloads':
        //         return <FileDownloadComponent {...props}></FileDownloadComponent>;
        //     default:
        //         const Comp = ROUTE_TO_COMPONENT_MAP[pathname];
        //         if (Comp) {
        //             return <Comp {...props}></Comp>;
        //         }
        //         return <NotFoundPage></NotFoundPage>
        // }
    }

    loadRouteSpecificScripts(pathname: string) {
        if (BrowserUtils.hasWindow() && pathname.endsWith('downloads')) {
            try {
                this.scriptLoadService.loadGoogleDriveSave();
            } catch (e) {
                console.error(e);
            }
            try {
                this.scriptLoadService.loadDropbox();
            } catch (e) {
                console.error(e);
            }
        }
    }

    getTitle(pageProps) {
        const titleData = (pageProps.seoHeaderTagsJson || []).find(el => el.elementType == 'title');
        return (titleData && titleData.innerText && titleData.innerText.trim()) || 'XConvert.com'
    }

    getCustomHeaders(pageProps: any) {
        if (pageProps.seoHeaderTagsJson) {
            return <Fragment>
                {
                    (pageProps.seoHeaderTagsJson as any[]).map((el, index) => {
                        const attributes = el.elementAttributes || {};
                        if (attributes.content) {
                            attributes.content = StringUtils.removeNewLineAndMultipleContigeousWhiteSpaces(attributes.content);
                        }
                        const reactElement = createElement(el.elementType, {
                            key: `header-tag-${index}`,
                            ...attributes,
                        }, StringUtils.removeNewLineAndMultipleContigeousWhiteSpaces(el.innerText) || undefined);
                        return reactElement;
                    })
                }
                {

                    (pageProps?.path?.match(/product/i) || pageProps?.path?.match(/portal/i)) && <Fragment>
                        {/*
                          PayPal SDK is loaded CLIENT-SIDE via
                          ScriptLoadService.loadPayPal(), which delegates to
                          `loadScript` from the official @paypal/paypal-js
                          package. No <script> tag here — the package handles
                          injection, de-dup, SSR guards, client-id validation,
                          and returns the paypal namespace directly as a
                          Promise (side-steps the browser-extension class of
                          issue that was stripping our hand-rolled <script>
                          tag post-load).
                          Stripe.js is loaded via the @stripe/stripe-js npm module
                          (services/stripe-loader.ts singleton) — no static script tag.
                        */}
                    </Fragment>
                }
            </Fragment>
        }
        return <link href="https://www.google.com" />;
    }

    render() {
        const { Component, pageProps } = this.props;
        if (this.props.pageProps.statusCode === 404) {
            return <Component {...pageProps} />;
        }
        this.routeProps = appPropsToRouteProps(this.props);
        this.getComponent(this.routeProps)
        return <>
            <Head>
                <meta name="viewport" content="width=device-width,initial-scale=1" />
                {pageProps.path === 'downloads' && <meta name="robots" content="noindex" />}
                {/* <title>{this.getTitle(pageProps)}</title> */}
                {this.getCustomHeaders(pageProps)}
            </Head>
            <DIProvider container={container}>
                {/* <input key={'g-file-input'}
                    ref={el => this.fileInputButtonService.registerFileInputRef(el)}
                    type="file"
                    multiple={!this.props.singleFileSelect} hidden={true} accept={this.props.acceptedFileTypes}
                // onChange={($ev) => this.processFileSelectedEvent($ev as any)} 
                /> */}
                <Fragment>
                    {/* {pageProps.url && <HeaderComponent key="header" {...pageProps} />} */}
                    {/* UX-10 (W8-SHELL): the first Tab stop on /pdf-editor skips the 7 nav stops. */}
                    {hasSkipLink(pageProps.path) && <SkipLink />}
                    {pageProps.url && <Navbar toolsDropdownData={pageProps.toolsDropdownData} />}
                    <Component {...pageProps} />
                    <DownloadsCenter />
                    <EnforcementErrorModal />
                    <FooterV2Component></FooterV2Component>
                    <AnchorAd />
                    {false && pageProps.url && <FooterComponent
                        key="footer"
                        {...pageProps}
                        fileSelectBtnClicked={this.state.fileSelectBtnClicked}
                    ></FooterComponent>}
                </Fragment>
            </DIProvider>

            {/* Scripts moved to lazy load on user interaction for better initial load performance */}
        </>
    }
}

// Only uncomment this method if you have blocking data requirements for
// every single page in your application. This disables the ability to
// perform automatic static optimization, causing every page in your app to
// be server-side rendered.
//
// MyApp.getInitialProps = async (appContext: AppContext) => {
//   // calls page's `getInitialProps` and fills `appProps.pageProps`
//   const appProps = await App.getInitialProps(appContext);

//   return { ...appProps }
// }
